import React, { useCallback, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import { InlineBrowseSkeleton } from '@atlassian/rovo-platform-ui-components/inline-browse-loading';
import { BrowseSkills } from '@atlassian/rovo-skills/browse-skills';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import type { ScreenType } from '../../../common/utils/use-calculated-screen-type';
import { usePlaceholderOverrideDispatch } from '../../chat-input-placeholder-override-context/usePlaceholderOverrideDispatch';

import { messages } from './messages';

const SeeAllSkillsButton = React.lazy(() =>
	import('./SeeAllSkillsButton').then((module) => ({ default: module.SeeAllSkillsButton })),
);

const styles = cssMap({
	overlay: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface'),
	},
	overlayHidden: {
		display: 'none',
	},
	browseWrapper: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		overflowX: 'hidden',
	},
	browseHeaderContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		paddingTop: token('space.250'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.250'),
	},
	browseSearchBar: {
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
	},
	listSuspenseFallback: {
		flex: 1,
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
});

export type SkillsInlineBrowseProps = {
	screenType: ScreenType;
	closeInlineBrowse: () => void;
	onSelectSkill: (skill: Skill) => void;
};

export const SkillsInlineBrowse = ({
	screenType,
	closeInlineBrowse,
	onSelectSkill,
}: SkillsInlineBrowseProps): React.JSX.Element => {
	di(AtlassianRelayEnvironmentProvider);
	const { siteId } = useGlobalConfig();
	const setChatInputPlaceholderOverride = usePlaceholderOverrideDispatch();
	const { formatMessage } = useIntl();
	const [searchKeyword, setSearchKeyword] = useState('');
	const [isBrowseErrored, setIsBrowseErrored] = useState(false);

	const isSeeAllSkillsButtonEnabled = fg('rovo_chat_skills_library');

	const handleSkillHover = useCallback(
		(skillName: string | null) => {
			setChatInputPlaceholderOverride(skillName ?? '');
		},
		[setChatInputPlaceholderOverride],
	);

	return (
		<Box
			testId="skills-inline-browse-overlay"
			xcss={cx(styles.overlay, screenType !== 'skills' && styles.overlayHidden)}
		>
			<Box xcss={styles.browseWrapper}>
				<Box xcss={styles.browseHeaderContainer}>
					<Heading size="xlarge" as="h2">
						{formatMessage(messages.headerTitle)}
					</Heading>
					{isSeeAllSkillsButtonEnabled && siteId && (
						<Suspense
							interactionName="rovo-conversation-assistant-see-all-skills-button"
							fallback={null}
						>
							<SeeAllSkillsButton cloudId={siteId} />
						</Suspense>
					)}
				</Box>
				<Box xcss={styles.browseSearchBar}>
					<SearchInput
						placeholderText={formatMessage(messages.searchPlaceholder)}
						onSearch={setSearchKeyword}
						isCompact
						autoFocus
						isDisabled={isBrowseErrored}
					/>
				</Box>
				<ErrorBoundary
					fallbackRender={() => <ChatGenericError errorType="generic" />}
					onError={(error) => {
						setIsBrowseErrored(true);
						logException({
							exception: error,
							name: 'skillsBrowseRelay',
							tags: { siteId },
						});
					}}
				>
					<AtlassianRelayEnvironmentProvider>
						<Suspense
							interactionName="rovo-conversation-assistant-skills-inline-browse"
							fallback={
								<Box xcss={styles.listSuspenseFallback}>
									<InlineBrowseSkeleton />
								</Box>
							}
						>
							<BrowseSkills
								siteId={siteId}
								searchKeyword={searchKeyword}
								closeInlineBrowse={closeInlineBrowse}
								onSelectSkill={onSelectSkill}
								onSkillHover={handleSkillHover}
							/>
						</Suspense>
					</AtlassianRelayEnvironmentProvider>
				</ErrorBoundary>
			</Box>
		</Box>
	);
};
